import React, { JSX, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { getHasUnreadMessages } from '../../reduxStore/selectors/messages.selector';
import { ChatNewMessagesFloaterBadge } from '../chatNewMessagesFloaterBadge';
import { TogglerButtonIcon } from './TogglerButtonIcon';
import { TogglerButtonShape } from './TogglerButtonShape';
import classNames from 'classnames';
import { frameStateContext } from '../../contexts/frame/context';
import { configurationContext } from '../../contexts/configuration';
import { ChatState, ChatTogglerShape } from '@enuan/chat__utils';
import { styled } from '@mui/material/styles';
import { ITogglerButtonBaseProps } from './TogglerButton.types';
import { useBreakpoint } from 'src/hooks/useBreakpoint';

export const ChatToggler = styled(
	({
		className,
		onClick,
		...props
	}: Omit<
		Omit<JSX.IntrinsicElements['div'], 'onClick'> & ITogglerButtonBaseProps,
		'children'
	>) => {
		const {
			toggleChat,
			isOpen: { isOpen },
			isDetached,
		} = useContext(frameStateContext);
		const { toggler_button, state } = useContext(configurationContext);
		const { isMobile } = useBreakpoint();

		const isActive = state !== ChatState.OFF;

		const hasUnreadMessages = useSelector(getHasUnreadMessages) && isActive;

		const _onClick = useCallback(() => {
			onClick?.();
			toggleChat();
		}, [onClick, toggleChat]);

		const { shape_active, shape_inactive } = toggler_button ?? {};

		const togglerClassNames = classNames(
			className,
			'floater',
			'toggler-button',
			hasUnreadMessages && 'toggler-button--unread',
			`toggler-button--${isActive ? 'active' : 'inactive'}`,
			`toggler-button--${
				(isActive ? shape_active : shape_inactive) ?? ChatTogglerShape.ROUND
			}`,
			'toggler-button--attached',
			isMobile && 'toggler-button--mobile-attached',
			isOpen && 'toggler-button--chat-open',
		);

		return (
			<div onClick={_onClick} className={togglerClassNames}>
				<Shadow />
				<ChatNewMessagesFloaterBadge onClick={_onClick} />
				<TogglerButtonShape {...props} />
				<TogglerButtonIcon />
			</div>
		);
	},
	{
		label: 'ChatToggler',
		target: 'ChatToggler-root',
	},
)({
	minWidth: '6.36rem',
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	border: 0,
	padding: 0,
	margin: 0,
	userSelect: 'none',
	position: 'relative',
	marginTop: 'calc(2.73rem * 0.5)',
	pointerEvents: 'all',
	'& .floater__dot': {
		width: '12%',
		pointerEvents: 'none',
		'&:after': {
			content: '""',
			display: 'block',
			paddingBottom: '100%',
		},
	},
	'& .toggler-button__shape': {
		width: '100%',
		'&:after': {
			content: '""',
			display: 'block',
			paddingBottom: '100%',
		},
	},
	'&.toggler-button--round': {
		'& .toggler-button__shape': {
			borderRadius: '50%',
			borderWidth: '1px',
			borderStyle: 'solid',
			borderColor: 'var(--toggler_border)',
		},
		'& .floater__dot': {
			bottom: '19%',
			right: '17%',
		},
	},
	'&.toggler-button--balloon': {
		'& .toggler-button__shape': {
			position: 'relative',
			flex: '1 1 0',
			minWidth: 0,
			alignSelf: 'stretch',
		},
		'& .toggler-button__shape-svg': {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
		},
		'& .floater__dot': {
			bottom: '19%',
			right: '17%',
		},
	},
	'& .floater__icon .balloon': {
		stroke: 'var(--theme--light-secondary)',
		strokeWidth: 2,
	},
	'& .floater__icon .dot': {
		fill: 'var(--theme--light-secondary)',
	},
	'&.toggler-button--inactive': {
		'& .floater__icon .balloon': {
			fill: 'var(--toggler--off_color)',
		},
		'&.toggler-button--round .toggler-button__shape': {
			backgroundColor: 'var(--toggler--off_color)',
		},
		'&.toggler-button--balloon .toggler-button__shape-svg': {
			fill: 'var(--toggler--off_color)',
		},
	},
	'&.toggler-button--active': {
		'& .floater__icon .balloon.primary': {
			fill: 'var(--toggler--primary)',
		},
		'& .floater__icon .balloon.secondary': {
			fill: 'var(--toggler--secondary)',
		},
		'&.toggler-button--round .toggler-button__shape': {
			backgroundColor: 'var(--toggler--primary)',
		},
		'&.toggler-button--balloon .toggler-button__shape-svg': {
			fill: 'var(--toggler--primary)',
		},
	},
	'&.toggler-button--unread': {
		'& .floater__icon .balloon.primary': {
			fill: 'var(--toggler--secondary)',
		},
		'& .floater__icon .balloon.secondary': {
			fill: 'var(--toggler--primary)',
		},
		'&.toggler-button--round .toggler-button__shape': {
			backgroundColor: 'var(--toggler--secondary)',
		},
		'&.toggler-button--balloon .toggler-button__shape-svg': {
			fill: 'var(--toggler--secondary)',
		},
	},
	'&:hover': {
		cursor: 'pointer',
	},
	'&:focus': {
		outline: 0,
	},
	'&:after': {
		content: '""',
		display: 'block',
		paddingBottom: '100%',
	},
	'&.toggler-button--mobile-attached': {
		maxWidth: '6.36rem',
		maxHeight: '6.36rem',
		marginTop: 'calc(2.27rem * 0.5)',
	},
	'&.toggler-button--chat-open': {
		'& .floater__icon': {
			transform: 'scale(0)',
			'& .dot': {
				fill: 'var(--theme--light-secondary)',
			},
		},
		'& .floater__dot': {
			transform: 'scale(1)',
		},
		'&.toggler-button--round .toggler-button__shape': {
			backgroundColor: 'var(--toggler--secondary)',
		},
		'&.toggler-button--balloon .toggler-button__shape-svg': {
			fill: 'var(--toggler--secondary)',
		},
	},
	'&.toggler-button--attached': {
		maxWidth: '6.36rem',
	},
});

/**
 * Decorative radial glow rendered behind the toggler button. Positioned absolutely
 * relative to the toggler (which is `position: relative`) with a negative `inset`
 * so it extends beyond the button on all sides without any hardcoded size token.
 * Overflows visibly because neither the toggler nor `.chat` clip their contents.
 *
 * Gradient origin matches the screen corner where the toggler sits, driven by the
 * `.chat--right` / `.chat--left` ancestor classes — no JS needed to switch sides.
 *
 * Hidden in detached mode where the widget is embedded in a custom host.
 */
const Shadow = styled('div', { target: 'shadow', label: 'Shadow' })({
	position: 'absolute',
	inset: '-200%',
	zIndex: -1,
	pointerEvents: 'none',
	'.chat--right &': {
		background:
			'radial-gradient(at bottom right, var(--chat__shadow_background) 0%, rgba(255, 255, 255, 0) 70%)',
	},
	'.chat--left &': {
		background:
			'radial-gradient(at bottom left, var(--chat__shadow_background) 0%, rgba(255, 255, 255, 0) 70%)',
	},
	'.chat--detached &': {
		display: 'none',
	},
});
